import {
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
	IServerSideDatasource,
	IServerSideGetRowsParams,
	ViewportChangedEvent
} from '@ag-grid-community/core';
import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, fromEvent, PartialObserver, Subscription } from 'rxjs';
import { LuceneExpression, toString as luceneToString } from 'lucene';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { CommonColDef, DateColDef, LinkColDef } from '../../../shared/datacenter/column-defs';
import { CommonGridOptions, serverSideAgGridModules } from '../../../shared/datacenter/grid-options';
import { OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { LocationService } from '../../../shared/services/location.service';
import { TranslationService } from '../../../shared/services/translation.service';
import {
	AuctionOffer as FullAuctionOffer,
	AUCTION_OFFER_FIELD,
	Bundle as FullBundle,
	Bid as FullBid
} from '../../models/auction-offer';
import { AUCTION_SERVICE, AuctionOfferService } from '../../services/auction-offer.service';
import { TextFilter } from '../../../shared/models/ag-grid';
import { createTextFilter } from '../../../shared/services/datacenter-filter';
import {
	DatacenterDateRangeFilterComponent
} from '../../../shared/datacenter/filter-type/datacenter-date-range-filter.component';
import { ColumnType } from '../../../shared/datacenter/column-type';
import { DatacenterTextFilterComponent } from '../../../shared/datacenter/filter-type/datacenter-text-filter.component';
import {
	DatacenterNumberFilterComponent
} from '../../../shared/datacenter/filter-type/datacenter-number-filter.component';
import filterIcon from '../../../shared/svg/filters.svg';
import { StepStatus, StepperComponent } from '../../../shared/components/stepper/stepper.component';
import { CheckboxHeaderComponent } from '../../../shared/datacenter/header-components/checkbox-header.component';
import { UserService } from '../../../shared/services/user.service';
import { User } from '../../../shared/models/user';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';

// replace circular references

type Bid = Omit<FullBid, 'bundle'>;
type Bundle = Omit<FullBundle, 'auctionOffer' | 'bids'> & { bids: Bid[] };
type AuctionOffer = Omit<FullAuctionOffer, 'bundles'> & { bundles: Bundle[] };

class AuctionDatasource implements IServerSideDatasource {
	private sub?: Subscription;
	private readonly rowsCountSubject = new BehaviorSubject(<number | undefined>undefined);
	private readonly rowsFilterSubject = new BehaviorSubject(<string | undefined>undefined);
	private readonly rowsSubject = new BehaviorSubject(<AuctionOffer[] | undefined>undefined);

	constructor(
		readonly auctionOfferService: AuctionOfferService,
	) {}

	subscribeToRowsCount(observer: PartialObserver<number | undefined>) {
		return this.rowsCountSubject.subscribe(observer);
	}

	subscribeToRows(observer: PartialObserver<AuctionOffer[] | undefined>) {
		return this.rowsSubject.subscribe(observer);
	}

	subscribeToRowsFilter(observer: PartialObserver<string | undefined>) {
		return this.rowsFilterSubject.subscribe(observer);
	}

	destroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}

	getRows(params: IServerSideGetRowsParams<AuctionOffer>) {
		const { request } = params;
		let criterionFilterNode;
		this.rowsFilterSubject.next(undefined);
		for (const filterModel of Object.values(<Record<string, TextFilter> | null> request.filterModel ?? {})) {
			const field = 'name';
			const criterionFilterValueNode = createTextFilter(filterModel, field);
			if (!criterionFilterNode) {
				criterionFilterNode = criterionFilterValueNode;
				continue;
			}
			criterionFilterNode = {
				left: criterionFilterNode,
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			};
		}
		const filter = luceneToString(<LuceneExpression>criterionFilterNode);
		if (this.sub) {
			this.sub.unsubscribe();
		}
		this.rowsCountSubject.next(undefined);
		this.rowsSubject.next(undefined);
		this.sub = this.auctionOfferService.getAuctionOffers(
			AUCTION_OFFER_FIELD,
			filter,
			params.request.startRow ?? 0,
			250,
			new OrderBy('id', OrderByDirection.ASC),
			false
		).subscribe({
			next: res => {
				if (res.count < 1) {
					params.api.showNoRowsOverlay();
				}
				const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow
					? res.count
					: -1;
				params.success({ rowData: res.results, rowCount: lastRow });
				this.rowsCountSubject.next(res.count);
				this.rowsSubject.next(res.results);
			},
			error: (err: unknown) => {
				console.error('error requesting auction offer', err);
				this.rowsCountSubject.next(undefined);
				this.rowsSubject.next(undefined);
				params.fail();
			}
		});
	}
}

const resizeObservable = fromEvent(globalThis, 'resize');

@Component({
	selector: 'copo-auction-offer-selection',
	templateUrl: './auction-offer-selection.component.html',
	styleUrl: './auction-offer-selection.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		PageHeaderComponent,
		StepperComponent,
		TranslateModule,

		MatFormFieldModule,

		MatSelectModule,
		MatIconModule,

		MatButtonModule,
		AgGridModule,
		LoadingSpinnerComponent,
		NumberPipe
	]
})
export class AuctionOfferSelectionComponent implements OnDestroy {
	count: number | undefined;

	resizeSub?: Subscription;
	rowCountsSub?: Subscription;
	user: User;
	soldTos?: string[];
	soldToSub: Subscription;
	isSoldToLoading = true;

	readonly agGridModules = serverSideAgGridModules;
	gridApi?: GridApi<AuctionOffer>;
	auctionDatasource?: AuctionDatasource;
	overlayNoRowsTemplate: string;

	readonly steps;

	offerGridOptions: CommonGridOptions<AuctionOffer>;

	constructor(
		private readonly auctionOfferService: AuctionOfferService,
		private readonly translationService: TranslationService,
		private readonly locationService: LocationService,
		private readonly userService: UserService,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('auction.selection'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('auction.bids'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('auction.confirmation'),
				status: StepStatus.DISABLED,
			}
		];
		this.offerGridOptions = new CommonGridOptions<AuctionOffer>({
			translationService: this.translationService,
			rowModelType: 'serverSide',
			multiSortKey: 'ctrl',
			cacheBlockSize: 250,
			suppressRowClickSelection: true,
			domLayout: 'autoHeight',
			headerHeight: 70,
			// prevent canceled api calls when updating layout
			blockLoadDebounceMillis: 1,
			icons: {
				filter: filterIcon,
			},
			getRowId: ({ data }: { data: AuctionOffer }) => String(data.id),
			columnDefs: [
				new CommonColDef<AuctionOffer>({
					headerName: this.translationService.getTR('auction.mill'),
					field: 'mill.name',
					filter: DatacenterTextFilterComponent,
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: false },
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					suppressHeaderMenuButton: false,
				}),
				new CommonColDef<AuctionOffer>({
					headerName: this.translationService.getTR('auction.market'),
					field: 'market',
					filter: DatacenterTextFilterComponent,
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: false },
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					suppressHeaderMenuButton: false,
				}),
				new LinkColDef<AuctionOffer>({
					headerName: this.translationService.getTR('auction.name'),
					field: 'name',
					filter: DatacenterTextFilterComponent,
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: false },
					cellRendererParams: {
						routerLink: (params: ICellRendererParams<AuctionOffer, unknown>) => {
							if (!params.data) {
								return null;
							}
							const { data } = params;
							const soldToSelectedByDefault = this.soldTos ? this.soldTos[0] : '';
							return {
								commands: [
									'/auction',
									'bidding',
									'offer',
									data.id,
									soldToSelectedByDefault
								]
							};
						},
					},
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					suppressHeaderMenuButton: false,
					minWidth: 150
				}),
				new CommonColDef<AuctionOffer>({
					headerName: this.translationService.getTR('offer.weight'),
					field: 'weight',
					filter: DatacenterNumberFilterComponent,
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: false },
					sortable: true,
					columnType: ColumnType.NUMBER_COLUMN,
					suppressHeaderMenuButton: false
				}),
				new CommonColDef<AuctionOffer>({
					headerName: this.translationService.getTR('offer.product.family'),
					fields: ['families.family'],
					valueGetter: params => {
						const auctionOffer = params.data;
						return auctionOffer?.families
							&& auctionOffer.families.length === 1 ? auctionOffer.families.map(item => item.family)
							: auctionOffer?.families
								&& auctionOffer.families.length > 1
								? this.translationService.getTR('offer.multiple.families') : '--';
					},
					filter: DatacenterTextFilterComponent,
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: false },
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					suppressHeaderMenuButton: false
				}),
				new CommonColDef<AuctionOffer>({
					headerName: this.translationService.getTR('offer.sub.family'),
					field: 'families',
					valueGetter: params => {
						const auctionOffer = params.data;
						return auctionOffer?.families
							&& auctionOffer.families.length === 1 ? auctionOffer.families.map(item => item.subFamily)
							: auctionOffer?.families
								&& auctionOffer.families.length > 1
								? this.translationService.getTR('offer.multiple.families') : '--';
					},
					filter: DatacenterTextFilterComponent,
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: false },
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					suppressHeaderMenuButton: false
				}),
				new DateColDef<AuctionOffer>({
					headerName: this.translationService.getTR('offer.end.date'),
					field: 'endDate',
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'datetime' },
					filter: DatacenterDateRangeFilterComponent,
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: false },
					columnType: ColumnType.DATE_COLUMN,
					resizable: true,
					suppressHeaderMenuButton: false,
					width: 150,
					minWidth: 150,
					maxWidth: 150
				}),
				new CommonColDef<AuctionOffer>({
					headerName: this.translationService.getTR('offer.transport.condition'),
					field: 'transportInvoicingCondition',
					minWidth: 150,
					filter: DatacenterTextFilterComponent,
					headerComponent: CheckboxHeaderComponent,
					headerComponentParams: { showCheckbox: false },
					sortable: true,
					columnType: ColumnType.TEXT_COLUMN,
					suppressHeaderMenuButton: false
				}),
			]
		});
		this.user = this.userService.user;
		this.overlayNoRowsTemplate = this.translationService.getTR('auction-offer.now-rows');
		this.soldToSub = this.userService.getSoldTosTransac(AUCTION_SERVICE).subscribe({
			next: soldTos => {
				this.soldTos = soldTos;
			},
			complete: () => {
				this.isSoldToLoading = false;
			}
		});
	}

	onGridReady(params: GridReadyEvent<AuctionOffer>) {
		this.gridApi = params.api;
		this.auctionDatasource =
			new AuctionDatasource(this.auctionOfferService);
		this.rowCountsSub = this.auctionDatasource.subscribeToRowsCount({
			next: value => {
				this.count = value;
			}
		});
		params.api.setGridOption('serverSideDatasource', this.auctionDatasource);
		params.api.sizeColumnsToFit();
		this.resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	ngOnDestroy() {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
		if (this.rowCountsSub !== undefined) {
			this.rowCountsSub.unsubscribe();
		}
	}

	goBack() {
		this.locationService.go(-1);
	}
}
